import Cookies from "js-cookie";
import type { Store } from "vuex";
import type { Router } from "vue-router";
import { ErrorCode } from "@/Enums/ErrorCodes";
import { COOKIE_AUTH_TOKEN } from "@/constants/StorageKeys";

const API_BASE_URL = process.env.VUE_APP_API_URL || "/api";

let storeInstance: Store<any> | null = null;
let routerInstance: Router | null = null;

export function initHttpClient(store: Store<any>, router: Router) {
  storeInstance = store;
  routerInstance = router;
}

export interface ApiResponse<T> {
  data?: T;
  code?: string | null;
}

export interface ApiError {
  errorCode: string;
}

type HttpMethod = "GET" | "POST" | "PUT" | "DELETE" | "PATCH";

function buildQueryString(params?: object): string {
  if (!params) return "";
  const query = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value == null || value === "") continue;
    query.append(key, String(value));
  }
  const qs = query.toString();
  return qs ? `?${qs}` : "";
}

class HttpClient {
  private baseUrl: string;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
  }

  private getAuthHeaders(): Record<string, string> {
    const token = Cookies.get(COOKIE_AUTH_TOKEN);
    return token ? { Authorization: `Bearer ${token}` } : {};
  }

  private async handleResponse<T>(response: Response): Promise<T> {
    const contentType = response.headers.get("content-type");
    const isJson = contentType?.includes("application/json");

    if (response.status === 401) {
      if (isJson) {
        const errorData = await response.json();
        if (errorData.code) {
          throw { errorCode: errorData.code } as ApiError;
        }
      }

      if (storeInstance && routerInstance) {
        await storeInstance.dispatch("auth/logout", routerInstance);
        await storeInstance.dispatch("notificator/apiError", {
          errorCode: ErrorCode.UNAUTHORIZED,
        });
      }
      throw { errorCode: ErrorCode.UNAUTHORIZED } as ApiError;
    }

    if (!response.ok) {
      if (isJson) {
        const errorData = await response.json();
        throw { errorCode: errorData.code || ErrorCode.INTERNAL_ERROR } as ApiError;
      }
      throw { errorCode: ErrorCode.INTERNAL_ERROR } as ApiError;
    }

    if (isJson) {
      const json: ApiResponse<T> = await response.json();
      if (json.code) {
        throw { errorCode: json.code } as ApiError;
      }
      return json.data as T;
    }

    return {} as T;
  }

  private async request<T>(
    method: HttpMethod,
    endpoint: string,
    body?: unknown
  ): Promise<T> {
    try {
      const response = await fetch(`${this.baseUrl}${endpoint}`, {
        method,
        headers: {
          "Content-Type": "application/json",
          ...this.getAuthHeaders(),
        },
        body: body ? JSON.stringify(body) : undefined,
      });
      return this.handleResponse<T>(response);
    } catch (error: any) {
      if (error?.errorCode) throw error;
      throw { errorCode: ErrorCode.CONNECTION_ERROR } as ApiError;
    }
  }

  async get<T>(endpoint: string, params?: object): Promise<T> {
    return this.request<T>("GET", endpoint + buildQueryString(params));
  }

  async post<T>(endpoint: string, body?: unknown): Promise<T> {
    return this.request<T>("POST", endpoint, body);
  }

  async put<T>(endpoint: string, body?: unknown): Promise<T> {
    return this.request<T>("PUT", endpoint, body);
  }

  async delete<T>(endpoint: string, params?: object): Promise<T> {
    return this.request<T>("DELETE", endpoint + buildQueryString(params));
  }

  async patch<T>(endpoint: string, body?: unknown): Promise<T> {
    return this.request<T>("PATCH", endpoint, body);
  }

  async postFormData<T>(endpoint: string, formData: FormData): Promise<T> {
    try {
      const response = await fetch(`${this.baseUrl}${endpoint}`, {
        method: "POST",
        headers: {
          ...this.getAuthHeaders(),
        },
        body: formData,
      });
      return this.handleResponse<T>(response);
    } catch (error: any) {
      if (error?.errorCode) throw error;
      throw { errorCode: ErrorCode.CONNECTION_ERROR } as ApiError;
    }
  }
}

export const http = new HttpClient(API_BASE_URL);
export default http;
